import { Input } from '@shared/components/ui';
import { FileText, AlertCircle, Check, Loader2, Users } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@shared/hooks/useUser';

interface OrganizationMember {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface NameProps {
  data: {
    name: string;
    type: 'POSTGRESQL' | 'FILES';
    created_by?: string;
  };
  onChange: (data: unknown) => void;
  onNext: () => void;
  onBack?: () => void;
  editConfigId?: string;
  userRole?: string;
}

interface CheckResult {
  available: boolean;
  message?: string;
}

export function Name({
  data,
  onChange,
  onNext: _onNext,
  onBack: _onBack,
  editConfigId,
  userRole,
}: NameProps) {
  const { t } = useTranslation('backups');
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const { user } = useUser();

  const isEditMode = !!editConfigId;

  // Only ADMIN and OWNER can change the owner in edit mode
  const canChangeOwner = !isEditMode || userRole === 'ADMIN' || userRole === 'OWNER';

  // Fetch organization members for owner selection
  useEffect(() => {
    const fetchMembers = async () => {
      if (!user?.organization?.id) return;

      setLoadingMembers(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          `/api/v1/organizations/members?organization_id=${user.organization.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          // Filter out VIEWER roles as they can't be backup owners
          const eligibleMembers = (data.members || []).filter(
            (m: OrganizationMember) => m.role !== 'VIEWER'
          );
          setMembers(eligibleMembers);
        }
      } catch (error) {
        console.error('Failed to fetch organization members:', error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [user?.organization?.id]);

  // Set default owner to current user if not already set
  useEffect(() => {
    if (user?.id && !data.created_by && !isEditMode) {
      onChange({ ...data, created_by: user.id });
    }
  }, [user?.id, data.created_by, isEditMode, data, onChange]);

  // Check for duplicate names via API
  const checkDuplicateName = useCallback(
    async (name: string) => {
      if (!name || name.trim().length < 2) {
        setCheckResult(null);
        return;
      }

      setChecking(true);
      try {
        const token = localStorage.getItem('auth_token');
        const params = new URLSearchParams();
        params.append('name', name.trim());
        if (editConfigId) {
          params.append('exclude_id', editConfigId);
        }

        const response = await fetch(`/api/v1/backups/configs/check-name?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const result = await response.json();
          setCheckResult({
            available: result.available,
            message: result.message,
          });
        } else {
          setCheckResult(null);
        }
      } catch (error) {
        console.error('Failed to check name:', error);
        setCheckResult(null);
      } finally {
        setChecking(false);
      }
    },
    [editConfigId]
  );

  // Debounced name check
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (data.name && data.name.trim().length >= 2) {
      const timer = setTimeout(() => {
        checkDuplicateName(data.name);
      }, 500);
      setDebounceTimer(timer);
    } else {
      setCheckResult(null);
    }

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [data.name, checkDuplicateName]);

  const getTypeLabel = () => {
    return data.type === 'POSTGRESQL' ? 'PostgreSQL' : t('filters.type.files');
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-2xl flex items-start space-x-3">
        <FileText className="text-state-info-light dark:text-state-info-dark shrink-0" size={20} />
        <p className="text-xs font-medium text-state-info-light dark:text-state-info-dark leading-relaxed">
          {t('wizard.nameStep.description')}
          {isEditMode && t('wizard.nameStep.editNote')}
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label={t('wizard.nameStep.label')}
          placeholder={t('wizard.nameStep.placeholder', { type: getTypeLabel() })}
          value={data.name || ''}
          onChange={(e) => {
            const value = e.target.value;
            onChange({ ...data, name: value });
            setErrors({ ...errors, name: undefined });
          }}
          error={errors.name}
          autoFocus
        />

        {/* Name availability indicator */}
        {data.name && data.name.trim().length >= 2 && (
          <div className="flex items-center gap-2">
            {checking ? (
              <>
                <Loader2
                  size={16}
                  className="animate-spin text-text-subtle-light dark:text-text-subtle-dark"
                />
                <span className="text-xs text-text-muted-light dark:text-text-muted-dark">
                  {t('wizard.nameStep.checking')}
                </span>
              </>
            ) : checkResult ? (
              checkResult.available ? (
                <>
                  <Check size={16} className="text-state-ok-light dark:text-state-ok-dark" />
                  <span className="text-xs text-state-ok-light dark:text-state-ok-dark font-medium">
                    {t('wizard.nameStep.validations.available')}
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle
                    size={16}
                    className="text-state-danger-light dark:text-state-danger-dark"
                  />
                  <span className="text-xs text-state-danger-light dark:text-state-danger-dark font-medium">
                    {checkResult.message || t('wizard.nameStep.validations.inUse')}
                  </span>
                </>
              )
            ) : null}
          </div>
        )}

        {/* Owner Selection */}
        <div className="space-y-2">
          <label
            htmlFor="backup-config-owner"
            className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2"
          >
            <Users size={16} />
            {t('wizard.nameStep.ownerLabel')}
            {isEditMode && !canChangeOwner && (
              <span className="text-xs text-text-muted-light dark:text-text-muted-dark font-normal">
                {t('wizard.nameStep.ownerAdminOnly')}
              </span>
            )}
          </label>
          <select
            id="backup-config-owner"
            value={data.created_by || user?.id || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                onChange({ ...data, created_by: value });
              }
            }}
            disabled={!canChangeOwner || loadingMembers || members.length === 0}
            className="w-full !min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMembers ? (
              <option value={user?.id || ''}>{t('wizard.nameStep.loadingUsers')}</option>
            ) : members.length === 0 ? (
              <option value={user?.id || ''}>
                {user?.full_name || user?.email || t('wizard.nameStep.you')}
              </option>
            ) : (
              members.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.full_name || member.email}{' '}
                  {member.role === 'OWNER'
                    ? `(${t('wizard.nameStep.roleOwner')})`
                    : member.role === 'ADMIN'
                      ? `(${t('wizard.nameStep.roleAdmin')})`
                      : `(${t('wizard.nameStep.roleOperator')})`}
                  {member.user_id === user?.id ? ` - ${t('wizard.nameStep.you')}` : ''}
                </option>
              ))
            )}
          </select>
          <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
            {t('wizard.nameStep.ownerDescription')}
          </p>
        </div>

        <div className="p-4 bg-surface-2-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <p className="text-xs font-medium text-slate-600 dark:text-text-subtle-dark">
            <span className="font-bold">{t('wizard.nameStep.tipTitle')}</span>{' '}
            {t('wizard.nameStep.tipText')}
          </p>
          <ul className="mt-2 text-xs text-text-muted-light dark:text-text-muted-dark space-y-1 ml-4 list-disc">
            <li>{t('wizard.nameStep.examples.db1')}</li>
            <li>{t('wizard.nameStep.examples.db2')}</li>
            <li>{t('wizard.nameStep.examples.files1')}</li>
            <li>{t('wizard.nameStep.examples.db3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
